<template>
  <div class="sf-el configurator-choice-input">
    <div class="sf-configurator-choice">
      <label class="sf-el__label">
        {{ label }}
        <span v-if="required" class="sf-el__required">*</span>
      </label>
      <p v-if="caption" class="sf-el__caption">{{ caption }}</p>

      <!-- Select (dropdown) display mode -->
      <template v-if="layout === 'select' && selectionMode === 'single'">
        <!-- Always the custom combobox; it shows its own search field only when
             the list is long enough to scroll (native <select> can't host search).
             `autofilled` carries the auto-fill highlight onto the combobox trigger. -->
        <SearchableChoiceSelect
          :choices="choices"
          :model-value="modelValue"
          :show-pricing="showPricing"
          :label="label"
          :autofilled="isAutoFilled && !!modelValue"
          @update:model-value="handleSelectChange"
        />
        <p v-if="isAutoFilled && modelValue" class="sf-autofill-hint">
          {{ t('form.fields.configurator.autofilled') || 'Automatisch ingevuld' }}
        </p>
      </template>

      <!-- Standard radio/checkbox display modes -->
      <template v-else>
      <div v-if="add_search_bar" class="sf-search-bar">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Zoek een optie..."
          class="sf-search-input"
        />
      </div>

      <div
        class="sf-configurator-choice__grid"
        :class="{
          [`sf-configurator-choice__grid--${layout}`]: layout,
          'sf-configurator-choice__grid--card': upsellCardMode,
          'sf-configurator-choice__grid--autofilled':
            isAutoFilled && selectionMode === 'single' && modelValue,
        }"
      >
        <div
          v-for="(choice, index) in filteredChoices"
          :key="choice.id"
          class="choice"
          :class="{
            'is-selected': isSelected(choice.id),
            'is-disabled': choice.disabled,
            'choice--has-image': showImages && choice.image,
            'choice--has-pricing': showPricing && choice.price !== undefined,
            'choice--card': upsellCardMode,
          }"
        >
          <!-- Hidden Input (visually hidden, only for form semantics) -->
          <input
            :type="selectionMode === 'single' ? 'radio' : 'checkbox'"
            :name="name"
            :value="choice.id"
            :checked="isSelected(choice.id)"
            :disabled="choice.disabled"
            class="choice-input"
            :id="`${choice.id}-choice-${index}`"
            @click.stop.prevent
            tabindex="-1"
            aria-hidden="true"
          />

          <!-- Card Layout (for upsells) -->
          <div
            v-if="upsellCardMode"
            class="choice__card"
            :class="{ 'choice__card--clickable': true }"
            @click="!choice.disabled && handleInput(choice.id, $event)"
          >
            <!-- Card Image -->
            <div v-if="showImages && choice.image" class="choice__card-image">
              <img :src="choice.image" :alt="choice.content" />
            </div>
            <div v-else class="choice__card-image choice__card-image--placeholder">
              <BaseIcon name="package" size="32" />
            </div>

            <!-- Card Content -->
            <div class="choice__card-content">
              <div class="choice__card-header">
                <div class="choice__card-info">
                  <h4 class="choice__card-title">{{ choice.content }}</h4>
                  <div v-if="showPricing && choice.price !== undefined" class="choice__card-price">
                    {{ formatPrice(choice.price) }}
                  </div>
                </div>
                <!-- Selection Indicator (same as standard layout) -->
                <div class="choice__indicator">
                  <div v-if="selectionMode === 'single'" class="choice__radio">
                    <div class="choice__radio-inner"></div>
                  </div>
                  <div v-else class="choice__checkbox">
                    <BaseIcon name="check" size="16" />
                  </div>
                </div>
              </div>

              <div v-if="choice.description" class="choice__card-description">
                {{ choice.description }}
              </div>

              <!-- USPs -->
              <ul
                v-if="showUsps && choice.usps?.length && !choice.disabled"
                class="choice__card-usps"
              >
                <li v-for="usp in choice.usps.slice(0, 3)" :key="usp" class="choice__card-usp">
                  <BaseIcon name="check-circle" size="16" />
                  {{ usp }}
                </li>
              </ul>

              <!-- Quantity Selector (if allowQuantity is true) -->
              <div
                v-if="choice.allowQuantity && isSelected(choice.id)"
                class="choice__card-quantity-wrapper"
                @click.stop
              >
                <div
                  class="choice__card-quantity"
                  :class="{ 'choice__card-quantity--error': quantityErrors[choice.id] }"
                >
                  <button
                    type="button"
                    @click="decreaseQuantity(choice.id)"
                    :disabled="getQuantity(choice.id) <= (choice.quantityConfig?.min || 1)"
                    class="choice__qty-btn"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    :value="getQuantity(choice.id)"
                    @input="updateQuantity(choice.id, $event.target.value, false)"
                    @blur="updateQuantity(choice.id, $event.target.value, true)"
                    :min="choice.quantityConfig?.min || 1"
                    :max="choice.quantityConfig?.max || 999"
                    class="choice__qty-input"
                    :class="{ 'choice__qty-input--error': quantityErrors[choice.id] }"
                  />
                  <button
                    type="button"
                    @click="increaseQuantity(choice.id)"
                    :disabled="getQuantity(choice.id) >= (choice.quantityConfig?.max || 999)"
                    class="choice__qty-btn"
                  >
                    +
                  </button>
                </div>
                <div v-if="quantityErrors[choice.id]" class="choice__qty-error">
                  {{ quantityErrors[choice.id] }}
                </div>
              </div>
            </div>
          </div>

          <!-- Standard Layout (non-card) -->
          <label
            v-else
            :for="`${choice.id}-choice-${index}`"
            class="choice__label"
            @click.stop="!choice.disabled && handleInput(choice.id, $event)"
          >
            <!-- Selection Indicator -->
            <div class="choice__indicator">
              <div v-if="selectionMode === 'single'" class="choice__radio">
                <div class="choice__radio-inner"></div>
              </div>
              <div v-else class="choice__checkbox">
                <BaseIcon name="check" size="16" />
              </div>
            </div>
            <!-- Choice Image -->
            <div v-if="showImages && choice.image" class="choice__image">
              <img :src="choice.image" :alt="choice.content" />
            </div>

            <div class="choice__content">
              <!-- Choice Text Content -->
              <div class="choice__text">
                <div class="choice__title">
                  {{ choice.content }}
                  <span v-if="showPricing && choice.price !== undefined" class="choice__price">
                    + {{ formatPrice(choice.price) }}
                  </span>
                </div>
                <span v-if="choice.description" class="choice__description">
                  {{ choice.description }}
                </span>

                <!-- Unavailable message for matrix pricing -->
                <span v-if="choice.disabled" class="choice__unavailable">
                  Niet beschikbaar bij huidige configuratie
                </span>

                <!-- USPs -->
                <ul v-if="showUsps && choice.usps?.length && !choice.disabled" class="choice__usps">
                  <li v-for="usp in choice.usps.slice(0, 3)" :key="usp" class="choice__usp">
                    <BaseIcon name="check-circle" size="16" />
                    {{ usp }}
                  </li>
                </ul>
              </div>
            </div>
          </label>
        </div>
      </div>
      <p
        v-if="isAutoFilled && selectionMode === 'single' && modelValue"
        class="sf-autofill-hint"
      >
        {{ t('form.fields.configurator.autofilled') || 'Automatisch ingevuld' }}
      </p>
      </template>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch } from 'vue';
  import { useI18n } from '@shared/composables/useI18n';
  import BaseIcon from '../../BaseIcon.vue';
  import SearchableChoiceSelect from './SearchableChoiceSelect.vue';

  export default {
    name: 'ConfiguratorChoiceElement',

    components: {
      BaseIcon,
      SearchableChoiceSelect,
    },

    props: {
      label: {
        type: String,
        required: true,
      },
      description: String,
      caption: String,
      choices: {
        type: Array,
        required: true,
      },
      name: {
        type: String,
        required: true,
      },
      modelValue: {
        type: [String, Array],
        default: null,
      },
      display: {
        type: String,
        default: 'grid',
        deprecated: true, // Mark as deprecated, will be removed
      },
      required: {
        type: Boolean,
        default: false,
      },
      add_search_bar: {
        type: Boolean,
        default: false,
      },
      // CPQ-specific props
      selectionMode: {
        type: String,
        default: 'single',
        validator: (value) => ['single', 'multiple'].includes(value),
      },
      showImages: {
        type: Boolean,
        default: true,
      },
      showUsps: {
        type: Boolean,
        default: true,
      },
      showPricing: {
        type: Boolean,
        default: true,
      },
      layout: {
        type: String,
        default: 'grid',
        validator: (value) => ['grid', 'list', 'compact', 'select'].includes(value),
      },
      columns: {
        type: Number,
        default: 2,
      },
      upsellCardMode: {
        type: Boolean,
        default: false,
      },
      // True when this field's value was auto-filled from an identical sibling's
      // first pick (CU-869dz0un3) — drives the "Automatisch ingevuld" hint.
      isAutoFilled: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['update:modelValue', 'update:quantity'],

    setup(props, { emit }) {
      const { t } = useI18n();
      const searchQuery = ref('');
      const showAllChoices = ref(false);
      // Store quantities for each selected upsell (key: choiceId, value: quantity)
      const quantities = ref({});
      // Store validation errors for each quantity input (key: choiceId, value: error message)
      const quantityErrors = ref({});

      const isSelected = (choiceId) => {
        if (props.selectionMode === 'single') {
          return props.modelValue === choiceId;
        }

        // Multiple selection: handle both old format (array of IDs) and new format (array of objects)
        if (Array.isArray(props.modelValue)) {
          if (
            props.modelValue.length > 0 &&
            typeof props.modelValue[0] === 'object' &&
            props.modelValue[0].id
          ) {
            // New format: array of objects
            return props.modelValue.some((item) => item.id === choiceId);
          } else {
            // Old format: array of IDs
            return props.modelValue.includes(choiceId);
          }
        }

        return false;
      };

      // Get quantity for a choice (defaults to quantityConfig.default or 1)
      const getQuantity = (choiceId) => {
        if (quantities.value[choiceId] !== undefined) {
          return quantities.value[choiceId];
        }
        // Find choice to get default quantity
        const choice = props.choices.find((c) => c.id === choiceId);
        if (choice?.quantityConfig?.default) {
          return choice.quantityConfig.default;
        }
        return 1;
      };

      // Validate quantity
      const validateQuantity = (choiceId, value) => {
        const choice = props.choices.find((c) => c.id === choiceId);
        if (!choice?.allowQuantity || !choice?.quantityConfig) {
          return null; // No validation needed
        }

        const numValue = parseInt(value, 10);
        const min = choice.quantityConfig.min || 1;
        const max = choice.quantityConfig.max || 999;

        if (isNaN(numValue)) {
          return 'Voer een geldig getal in';
        }

        if (numValue < min) {
          return `Minimum is ${min}`;
        }

        if (numValue > max) {
          return `Maximum is ${max}`;
        }

        return null; // No error
      };

      // Update quantity for a choice
      const updateQuantity = (choiceId, value, forceClamp = false) => {
        const numValue = parseInt(value, 10);
        const choice = props.choices.find((c) => c.id === choiceId);
        const min = choice?.quantityConfig?.min || 1;
        const max = choice?.quantityConfig?.max || 999;

        // Validate
        const error = validateQuantity(choiceId, value);
        quantityErrors.value[choiceId] = error || null;

        // If there's an error and we're not forcing clamp (e.g., on blur), don't update yet
        if (error && !forceClamp) {
          // Allow user to type, but don't update modelValue
          return;
        }

        // If forcing clamp (on blur) or no error: clamp and update
        if (isNaN(numValue) || numValue < 1) {
          if (forceClamp) {
            // On blur: set to minimum
            const clampedValue = min;
            quantities.value[choiceId] = clampedValue;
            quantityErrors.value[choiceId] = null;
            updateModelValue(choiceId, clampedValue);
          } else {
            quantityErrors.value[choiceId] = 'Voer een geldig getal in';
          }
          return;
        }

        // Clamp value to min/max
        const clampedValue = Math.max(min, Math.min(max, numValue));
        quantities.value[choiceId] = clampedValue;
        quantityErrors.value[choiceId] = null; // Clear error

        // Update modelValue
        updateModelValue(choiceId, clampedValue);
      };

      // Helper to update modelValue
      const updateModelValue = (choiceId, quantity) => {
        // For upsells: update the modelValue to include the new quantity
        if (props.upsellCardMode && props.selectionMode === 'multiple') {
          const currentValue = props.modelValue;
          if (Array.isArray(currentValue)) {
            const updatedValue = currentValue.map((item) => {
              if (typeof item === 'object' && item.id === choiceId) {
                return { ...item, quantity };
              }
              return item;
            });
            emit('update:modelValue', updatedValue);
          }
        } else {
          // For non-upsell or single selection: emit quantity event (backwards compatibility)
          emit('update:quantity', {
            choiceId,
            quantity,
            fieldName: `${props.name}_${choiceId}_quantity`,
          });
        }
      };

      // Increase quantity
      const increaseQuantity = (choiceId) => {
        const current = getQuantity(choiceId);
        const choice = props.choices.find((c) => c.id === choiceId);
        const max = choice?.quantityConfig?.max || 999;
        if (current < max) {
          updateQuantity(choiceId, current + 1, true); // Force clamp to ensure valid value
        }
      };

      // Decrease quantity
      const decreaseQuantity = (choiceId) => {
        const current = getQuantity(choiceId);
        const choice = props.choices.find((c) => c.id === choiceId);
        const min = choice?.quantityConfig?.min || 1;
        if (current > min) {
          updateQuantity(choiceId, current - 1, true); // Force clamp to ensure valid value
        }
      };

      // Initialize quantities when choices are selected (for upsells)
      watch(
        () => props.modelValue,
        (newValue) => {
          if (props.upsellCardMode && props.selectionMode === 'multiple') {
            if (Array.isArray(newValue)) {
              // Handle new format (array of objects)
              if (newValue.length > 0 && typeof newValue[0] === 'object' && newValue[0].id) {
                newValue.forEach((item) => {
                  if (item.id && item.quantity !== undefined) {
                    quantities.value[item.id] = item.quantity;
                  }
                });
                // Remove quantities for deselected items
                const selectedIds = newValue.map((item) => item.id);
                Object.keys(quantities.value).forEach((id) => {
                  if (!selectedIds.includes(id)) {
                    delete quantities.value[id];
                  }
                });
              } else {
                // Handle old format (array of IDs) - convert to new format
                const selectedIds = newValue;
                selectedIds.forEach((id) => {
                  if (quantities.value[id] === undefined) {
                    const choice = props.choices.find((c) => c.id === id);
                    if (choice?.allowQuantity) {
                      quantities.value[id] = choice.quantityConfig?.default || 1;
                    }
                  }
                });
                // Remove quantities for deselected items
                Object.keys(quantities.value).forEach((id) => {
                  if (!selectedIds.includes(id)) {
                    delete quantities.value[id];
                  }
                });
              }
            }
          }
        },
        { immediate: true }
      );

      // Helper: Convert array of IDs to array of objects (for upsells with quantities)
      const convertToUpsellObjects = (ids) => {
        if (!props.upsellCardMode || !Array.isArray(ids)) {
          return ids; // Return as-is for non-upsell or non-array values
        }

        return ids.map((id) => {
          const choice = props.choices.find((c) => c.id === id);

          // Only add quantity if allowQuantity is true
          if (choice?.allowQuantity) {
            const existingQuantity = quantities.value[id];
            const quantity =
              existingQuantity !== undefined
                ? existingQuantity
                : choice?.quantityConfig?.default || 1;

            return {
              id: id,
              quantity: quantity,
            };
          } else {
            // For upsells without quantity, just return the ID as object (for consistency)
            // But we could also just return the ID - let's use object format for consistency
            return {
              id: id,
              quantity: 1, // Default to 1 for backwards compatibility, but won't be used
            };
          }
        });
      };

      // Helper: Convert array of objects to array of IDs (for reading from formData)
      const convertFromUpsellObjects = (value) => {
        if (!props.upsellCardMode) {
          return value; // Return as-is for non-upsell
        }

        if (Array.isArray(value)) {
          // Check if it's array of objects or array of IDs
          if (value.length > 0 && typeof value[0] === 'object' && value[0].id) {
            // Array of objects: extract IDs and restore quantities
            value.forEach((item) => {
              if (item.id && item.quantity !== undefined) {
                quantities.value[item.id] = item.quantity;
              }
            });
            return value.map((item) => item.id);
          }
          // Array of IDs (old format or non-upsell)
          return value;
        }
        return value;
      };

      const handleSelectChange = (choiceId) => {
        emit('update:modelValue', choiceId);
      };

      // Simplified input handler like SingleChoiceElement
      const handleInput = (choiceId, event) => {
        // Prevent default behavior and stop propagation
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }

        if (props.selectionMode === 'single') {
          // Single selection - for radio buttons, always select the clicked one
          emit('update:modelValue', choiceId);
        } else {
          // Multiple selection - checkbox behavior
          // Handle both old format (array of IDs) and new format (array of objects)
          const currentValue = props.modelValue;
          let currentSelection = [];

          if (Array.isArray(currentValue)) {
            if (
              currentValue.length > 0 &&
              typeof currentValue[0] === 'object' &&
              currentValue[0].id
            ) {
              // New format: array of objects
              currentSelection = currentValue.map((item) => item.id);
            } else {
              // Old format: array of IDs
              currentSelection = [...currentValue];
            }
          }

          if (currentSelection.includes(choiceId)) {
            // Remove from selection
            const index = currentSelection.indexOf(choiceId);
            currentSelection.splice(index, 1);
            // Also remove quantity
            delete quantities.value[choiceId];
          } else {
            // Add to selection
            currentSelection.push(choiceId);
            // Initialize quantity if allowQuantity is true
            const choice = props.choices.find((c) => c.id === choiceId);
            if (choice?.allowQuantity && quantities.value[choiceId] === undefined) {
              quantities.value[choiceId] = choice.quantityConfig?.default || 1;
            }
          }

          // Convert to array of objects for upsells, or keep as array of IDs for others
          const newValue = props.upsellCardMode
            ? convertToUpsellObjects(currentSelection)
            : currentSelection;

          if (props.name && props.name.includes('upsells')) {
            console.log('📤 Emitting update:modelValue with:', newValue);
          }
          emit('update:modelValue', newValue);
        }
      };

      const filteredChoices = computed(() => {
        if (!props.add_search_bar) {
          return props.choices || [];
        }

        if (!searchQuery.value.trim()) {
          if (showAllChoices.value) {
            return props.choices || [];
          } else {
            return props.choices ? props.choices.slice(0, 12) : [];
          }
        }

        const query = searchQuery.value.toLowerCase().trim();
        const filtered = props.choices
          ? props.choices.filter(
              (choice) =>
                choice.content.toLowerCase().includes(query) ||
                (choice.description && choice.description.toLowerCase().includes(query)) ||
                (choice.usps && choice.usps.some((usp) => usp.toLowerCase().includes(query)))
            )
          : [];

        return filtered.slice(0, 12);
      });

      const shouldShowMoreButton = computed(() => {
        if (!props.add_search_bar || showAllChoices.value || searchQuery.value.trim()) {
          return false;
        }
        const totalChoices = props.choices ? props.choices.length : 0;
        return totalChoices > 12;
      });

      const remainingChoicesCount = computed(() => {
        const totalChoices = props.choices ? props.choices.length : 0;
        return Math.max(0, totalChoices - 12);
      });

      const formatPrice = (price) => {
        if (price === 0) return 'Gratis';
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
        }).format(price);
      };

      return {
        t,
        searchQuery,
        showAllChoices,
        isSelected,
        handleInput,
        handleSelectChange,
        filteredChoices,
        shouldShowMoreButton,
        remainingChoicesCount,
        formatPrice,
        getQuantity,
        updateQuantity,
        increaseQuantity,
        decreaseQuantity,
        quantityErrors,
        validateQuantity,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@shared/assets/css/base/mixins.scss';
  @import './styles/choices.scss';

  .sf-select {
    width: 100%;
    padding: 0.625rem 2.5rem 0.625rem 0.75rem;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--cs-text-body, #374151);
    background-color: var(--cs-input-default-background, #fff);
    border: 1px solid var(--cs-input-default-border, #d1d5db);
    border-radius: var(--cs-border-radius, 0.5rem);
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 1rem;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: var(--cs-input-hover-border, #9ca3af);
    }

    &:focus {
      outline: none;
      border-color: var(--cs-brand-primary, #3b82f6);
      box-shadow: 0 0 0 3px var(--cs-brand-primary-light, rgba(59, 130, 246, 0.15));
    }

    &--autofilled {
      // Gentle, theme-safe highlight (color-mix stays light on any brand).
      // Plain fallbacks first so browsers without color-mix() (e.g. Safari
      // < 16.2, older webviews) still show a visible highlight instead of
      // dropping the declaration entirely.
      border-color: var(--cs-brand-primary, #3b82f6);
      border-color: color-mix(
        in srgb,
        var(--cs-brand-primary, #3b82f6) 40%,
        var(--cs-input-default-border, #d1d5db)
      );
      background-color: var(--cs-input-default-background, #fff);
      background-color: color-mix(
        in srgb,
        var(--cs-brand-primary, #3b82f6) 6%,
        var(--cs-input-default-background, #fff)
      );
    }
  }

  .sf-autofill-hint {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    margin: 0.375rem 0 0;
    font-size: 0.8125rem;
    line-height: 1.2;
    color: var(--cs-text-muted, #6b7280);

    &::before {
      content: '';
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      background-color: var(--cs-brand-primary, #3b82f6);
    }
  }

  .sf-configurator-choice {
    &__grid {
      display: grid;
      gap: 1rem;

      // Layout modes
      &--grid {
        grid-template-columns: repeat(2, 1fr);

        @include md-down {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      &--list {
        grid-template-columns: 1fr;
        gap: 0.75rem;
      }

      &--compact {
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 0.75rem;
      }

      // Card layout (for upsells)
      &--card {
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 1rem;

        @include md-down {
          grid-template-columns: 1fr;
        }
      }

      // Soft highlight when this radio/list field was seeded by auto-fill.
      &--autofilled {
        padding: 0.5rem;
        margin: -0.5rem;
        border-radius: 0.5rem;
        border: 1px solid var(--cs-brand-primary, #3b82f6);
        border-color: color-mix(
          in srgb,
          var(--cs-brand-primary, #3b82f6) 40%,
          var(--cs-input-default-border, #d1d5db)
        );
        background-color: color-mix(
          in srgb,
          var(--cs-brand-primary, #3b82f6) 6%,
          transparent
        );
      }
    }
  }

  .choice {
    @include lg-down {
      width: auto;
    }
    &__indicator {
      position: absolute;
      top: 0.875rem;
      right: 0.75rem;
      .choice__radio,
      .choice__checkbox {
        position: relative;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
    }
    &--has-image {
      .choice__content {
        flex-direction: column;
      }

      .choice__text {
        padding: 1rem 0 0 0;
      }
    }

    &__label {
      display: block;
      width: 100%;
      height: 100%;
      cursor: pointer;
      margin: 0;
      padding: 1rem 2.75rem 1rem 1rem;
      @include lg-down {
        padding: 0.75rem;
      }
    }

    &__image {
      width: 5rem;
      height: 5rem;
      background: var(--cs-input-default-background);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: var(--cs-border-radius);
      border: 1px solid var(--cs-input-default-border);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      // @include lg-down {
      //   width: 100%;
      //   height: 120px;
      // }
    }

    &__content {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 1rem;
    }

    &__text {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      width: 100%;
    }

    &__title {
      font-weight: 600;
      color: var(--cs-text-heading);
      font-size: 1rem;
      line-height: 1.4;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      justify-content: space-between;
    }

    &__description {
      font-size: 0.875rem;
      color: var(--cs-text-body);
      line-height: 1.4;
      margin: 0;
    }

    &__usps {
      list-style: none;
      margin: 0.5rem 0 0 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    &__usp {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: var(--cs-text-body);

      .icon {
        color: var(--cs-brand-primary);
      }
    }

    &__price {
      font-size: 14px;
      font-weight: 700;
      color: var(--cs-brand-primary);
    }

    &__unavailable {
      display: block;
      font-size: 0.75rem;
      color: var(--cs-text-muted, #94a3b8);
      font-style: italic;
      margin-top: 0.25rem;
    }
  }

  .choice-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  // List layout specific styles
  .sf-configurator-choice__grid--list {
    .choice {
      &__content {
        flex-direction: row;
        align-items: center;
      }

      &__image {
        width: 80px;
        height: 80px;
        flex-shrink: 0;
        border-radius: var(--cs-choice-border-radius);
      }

      &__text {
        text-align: left;
      }
    }
  }

  // Compact layout
  .sf-configurator-choice__grid--compact {
    .choice {
      &__content {
        padding: 0.75rem;
        min-height: 60px;
      }

      &__image {
        width: 60px;
        height: 60px;
      }

      &__title {
        font-size: 0.875rem;
      }

      &__description {
        font-size: 0.8125rem;
      }

      &__usps {
        display: none; // Hide USPs in compact mode
      }
    }
  }

  // Card layout styles (for upsells)
  .choice--card {
    border: 1px solid var(--cs-input-default-border);
    border-radius: var(--cs-border-radius);
    background: var(--cs-input-default-background);
    transition: all 0.2s ease;
    overflow: hidden;

    &:hover {
      border-color: var(--cs-choice-hover-border);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    &.is-selected {
      border-color: var(--cs-choice-active-border);
      background: var(--cs-choice-active-background);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .choice__card {
      display: flex;
      flex-direction: row;
      gap: 1rem;
      padding: 1rem;

      &--clickable {
        cursor: pointer;
      }
    }

    .choice__card-image {
      width: 80px;
      height: 80px;
      flex-shrink: 0;
      border-radius: var(--cs-border-radius);
      overflow: hidden;
      background: var(--cs-background-primary);
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      &--placeholder {
        color: var(--cs-text-body);
        opacity: 0.5;
      }
    }

    .choice__card-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      min-width: 0;
    }

    .choice__card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 1rem;
    }

    .choice__card-info {
      flex: 1;
      min-width: 0;
    }

    .choice__card-title {
      margin: 0 0 0.25rem;
      font-size: 1rem;
      font-weight: 600;
      color: var(--cs-text-heading);
      line-height: 1.4;
    }

    .choice__card-price {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--cs-brand-primary);
    }

    // Use standard choice__indicator in card layout
    .choice__indicator {
      flex-shrink: 0;
      position: relative;
      top: 0;
      right: 0;
    }

    .choice__card-description {
      font-size: 0.875rem;
      color: var(--cs-text-body);
      line-height: 1.4;
      margin: 0;
    }

    .choice__card-usps {
      list-style: none;
      margin: 0.5rem 0 0 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    .choice__card-usp {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      color: var(--cs-text-body);
    }

    // Quantity selector wrapper
    .choice__card-quantity-wrapper {
      margin-top: 0.5rem;
    }

    // Quantity selector (similar to cart)
    .choice__card-quantity {
      display: flex;
      align-items: center;
      gap: 0;
      border: 1px solid var(--cs-input-default-border);
      border-radius: var(--cs-border-radius);
      overflow: hidden;
      width: fit-content;
      transition: border-color 0.15s ease;

      &--error {
        border-color: var(--red-500, #ef4444);
      }
    }

    .choice__qty-btn {
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      font-weight: 500;
      color: var(--cs-text-body);
      transition: background 0.15s ease;

      &:hover:not(:disabled) {
        background: var(--cs-background-primary);
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }

    .choice__qty-input {
      width: 48px;
      height: 32px;
      border: none;
      border-left: 1px solid var(--cs-input-default-border);
      border-right: 1px solid var(--cs-input-default-border);
      text-align: center;
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--cs-text-body);
      background: var(--cs-input-default-background);
      -moz-appearance: textfield;
      appearance: textfield;
      transition: background 0.15s ease;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        appearance: none;
        margin: 0;
      }

      &:focus {
        outline: none;
        background: var(--cs-background-primary);
      }

      &--error {
        color: var(--red-500, #ef4444);
        border-left-color: var(--red-500, #ef4444);
        border-right-color: var(--red-500, #ef4444);
      }
    }

    .choice__qty-error {
      margin-top: 0.25rem;
      font-size: 0.75rem;
      color: var(--red-500, #ef4444);
      line-height: 1.4;
    }
  }
</style>
